/**
 * Post-answer feedback for einbuergerungstest practice mode.
 *
 * Renders the correct/incorrect headline, the question + EN translation
 * auto-revealed, every option highlighted (correct=green, the user's wrong
 * pick=red, others muted), and the tip auto-expanded if one exists.
 *
 * Simulation mode uses a different shell — sims show no feedback per card.
 */
import { useEffect, useRef } from 'react';
import { Check, Info, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { EinbuergerungsPayload, OptionLetter } from '@/core/types';
import { cn } from '@/lib/utils';

import type { CardBackProps } from '../types';
import { QuestionImage } from './QuestionImage';

type Props = CardBackProps<EinbuergerungsPayload>;

export function CardBack({ card, correct, user_answer, isLastCard, onNext }: Props) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  const userLetter = user_answer as OptionLetter | null;

  return (
    <div className="border-border bg-card flex flex-col gap-5 rounded-2xl border p-6 shadow-sm sm:p-8">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        {card.external_id ? (
          <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
            Question {card.external_id}
            {card.payload.scope === 'state' && card.payload.state_code
              ? ` · ${card.payload.state_code}`
              : ''}
          </p>
        ) : (
          <span />
        )}
        <div
          className={cn(
            'inline-flex items-center gap-1.5 font-medium',
            correct ? 'text-success' : 'text-destructive',
          )}
        >
          {correct ? <Check className="size-5" /> : <X className="size-5" />}
          <span>{correct ? 'Correct' : 'Incorrect'}</span>
        </div>
      </header>

      <div>
        <h2 className="font-display text-xl leading-snug font-semibold sm:text-2xl">{card.de}</h2>
        <p className="text-muted-foreground mt-2 text-base italic sm:text-lg">
          {card.payload.question_en}
        </p>
      </div>

      {card.payload.has_image && card.external_id ? (
        <QuestionImage bamfId={card.external_id} size="md" />
      ) : null}

      {/* All 4 options in the canonical A-D order. */}
      <div className="flex flex-col gap-2.5">
        {card.payload.options.map((opt) => {
          const isCorrect = opt.letter === card.payload.correct_letter;
          const isUserPick = opt.letter === userLetter;
          return (
            <OptionResultRow
              key={opt.letter}
              letter={opt.letter}
              de={opt.de}
              en={opt.en}
              variant={isCorrect ? 'correct' : isUserPick ? 'wrong' : 'neutral'}
            />
          );
        })}
      </div>

      {card.payload.tip_en ? (
        <div className="border-primary/30 bg-primary/5 flex items-start gap-2 rounded-md border p-3 text-sm">
          <Info className="text-primary mt-0.5 size-4 shrink-0" />
          <span>{card.payload.tip_en}</span>
        </div>
      ) : null}

      <Button ref={buttonRef} size="lg" onClick={onNext} className="w-full">
        {isLastCard ? 'Finish session' : 'Next card →'}
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function OptionResultRow({
  letter,
  de,
  en,
  variant,
}: {
  letter: OptionLetter;
  de: string;
  en: string;
  variant: 'correct' | 'wrong' | 'neutral';
}) {
  const ring =
    variant === 'correct'
      ? 'border-success bg-success/10'
      : variant === 'wrong'
        ? 'border-destructive bg-destructive/10'
        : 'border-border bg-card opacity-70';

  const letterBg =
    variant === 'correct'
      ? 'bg-success text-success-foreground'
      : variant === 'wrong'
        ? 'bg-destructive text-destructive-foreground'
        : 'bg-muted text-foreground';

  const trailing =
    variant === 'correct' ? (
      <Check className="text-success size-4" />
    ) : variant === 'wrong' ? (
      <X className="text-destructive size-4" />
    ) : null;

  return (
    <div className={cn('flex items-start gap-3 rounded-lg border-2 p-4', ring)}>
      <span
        className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-semibold',
          letterBg,
        )}
      >
        {letter}
      </span>
      <span className="flex-1 leading-snug">
        <span className="block text-base">{de}</span>
        <span className="text-muted-foreground mt-1 block text-sm italic">{en}</span>
      </span>
      {trailing}
    </div>
  );
}
